import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Plus, Trash2 } from 'lucide-react';

import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { SaveBar } from '@/components/ui/save-bar';
import { useBrief, useUpdateBriefSlice } from '@/lib/brief';

export const Route = createFileRoute('/_app/brief/equipment')({
  staticData: { crumb: 'Sprzęt' },
  component: EquipmentPage,
});

const CATEGORIES = [
  'diagnostic',
  'surgical',
  'lab',
  'dental',
  'imaging',
  'monitoring',
] as const;

interface EquipmentItem {
  name: string;
  category: (typeof CATEGORIES)[number];
  year_acquired?: string;
  notes?: string;
}

interface FacilityShape {
  operating_rooms?: number;
  icu_stations?: number;
  kennels?: number;
  lab_onsite?: boolean;
}

function EquipmentPage() {
  const { data: brief } = useBrief();
  const update = useUpdateBriefSlice();

  const [items, setItems] = React.useState<EquipmentItem[]>(
    () => (brief?.equipment ?? []) as EquipmentItem[]
  );
  const [facility, setFacility] = React.useState<FacilityShape>(
    () => (brief?.facility ?? {}) as FacilityShape
  );
  const [dirty, setDirty] = React.useState(false);
  const [savedStatus, setSavedStatus] = React.useState<'idle' | 'saved' | 'error'>('idle');

  React.useEffect(() => {
    if (!dirty) {
      setItems((brief?.equipment ?? []) as EquipmentItem[]);
      setFacility((brief?.facility ?? {}) as FacilityShape);
    }
  }, [brief?.equipment, brief?.facility, dirty]);

  const updateItem = (idx: number, patch: Partial<EquipmentItem>) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
    setDirty(true);
  };

  const onSave = async () => {
    setSavedStatus('idle');
    try {
      await update.mutateAsync({
        patch: {
          equipment: items.filter((it) => it.name.trim().length >= 2),
          facility,
        },
      });
      setSavedStatus('saved');
      setDirty(false);
    } catch {
      setSavedStatus('error');
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
      className="space-y-8"
    >
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">Sprzęt</h1>
        <p className="mt-1 text-sm text-text-muted">
          Wyposażenie kliniki — sprzęt diagnostyczny i zabiegowy. Pokażemy w sekcji
          „Wyposażenie" na publicznej stronie. Wstaw przynajmniej 6 pozycji aby sekcja
          się pojawiła.
        </p>
      </header>

      <section className="space-y-5 rounded-[var(--radius-card)] bg-bg p-6 ring-1 ring-black/5">
        <h2 className="font-display text-base font-semibold text-text">Obiekt</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Field label="Sale operacyjne" htmlFor="operating_rooms" hint="liczba">
            <Input
              id="operating_rooms"
              type="number"
              min={0}
              value={facility.operating_rooms ?? ''}
              onChange={(e) => {
                setFacility((p) => ({
                  ...p,
                  operating_rooms: e.target.value ? Number(e.target.value) : undefined,
                }));
                setDirty(true);
              }}
            />
          </Field>
          <Field label="Stanowiska OIOM" htmlFor="icu_stations" hint="liczba">
            <Input
              id="icu_stations"
              type="number"
              min={0}
              value={facility.icu_stations ?? ''}
              onChange={(e) => {
                setFacility((p) => ({
                  ...p,
                  icu_stations: e.target.value ? Number(e.target.value) : undefined,
                }));
                setDirty(true);
              }}
            />
          </Field>
          <Field label="Boksy/klatki" htmlFor="kennels" hint="liczba">
            <Input
              id="kennels"
              type="number"
              min={0}
              value={facility.kennels ?? ''}
              onChange={(e) => {
                setFacility((p) => ({
                  ...p,
                  kennels: e.target.value ? Number(e.target.value) : undefined,
                }));
                setDirty(true);
              }}
            />
          </Field>
          <Field label="Laboratorium" htmlFor="lab_onsite">
            <label className="flex h-10 items-center gap-2 text-sm">
              <input
                id="lab_onsite"
                type="checkbox"
                checked={facility.lab_onsite ?? false}
                onChange={(e) => {
                  setFacility((p) => ({ ...p, lab_onsite: e.target.checked }));
                  setDirty(true);
                }}
                className="size-4 rounded border-border text-accent focus:ring-accent"
              />
              <span>na miejscu</span>
            </label>
          </Field>
        </div>
      </section>

      <section className="space-y-4 rounded-[var(--radius-card)] bg-bg p-6 ring-1 ring-black/5">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-base font-semibold text-text">
            Lista sprzętu
            <span className="ml-2 text-sm font-normal text-text-subtle tabular-nums">
              {items.length} / 50
            </span>
          </h2>
          <Button
            type="button"
            variant="secondary"
            disabled={items.length >= 50}
            onClick={() => {
              setItems((p) => [...p, { name: '', category: 'diagnostic' }]);
              setDirty(true);
            }}
          >
            <Plus className="size-4" aria-hidden="true" /> Dodaj sprzęt
          </Button>
        </div>

        <ul role="list" className="space-y-3">
          {items.map((it, idx) => (
            <li
              key={idx}
              className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_5rem_3rem]"
            >
              <Input
                placeholder="Nazwa sprzętu (np. Mindray BC-30Vet)"
                value={it.name}
                onChange={(e) => updateItem(idx, { name: e.target.value })}
              />
              <Select
                value={it.category}
                onChange={(e) =>
                  updateItem(idx, { category: e.target.value as EquipmentItem['category'] })
                }
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
              <Input
                placeholder="rok"
                inputMode="numeric"
                value={it.year_acquired ?? ''}
                onChange={(e) => updateItem(idx, { year_acquired: e.target.value || undefined })}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Usuń"
                onClick={() => {
                  setItems((p) => p.filter((_, i) => i !== idx));
                  setDirty(true);
                }}
              >
                <Trash2 className="size-4" aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      </section>

      <SaveBar
        saving={update.isPending}
        dirty={dirty}
        status={savedStatus}
        onCancel={() => {
          setItems((brief?.equipment ?? []) as EquipmentItem[]);
          setFacility((brief?.facility ?? {}) as FacilityShape);
          setDirty(false);
        }}
      />
    </form>
  );
}
